import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { fetchCategoryAndRedirect } from '../../actions/fetch-category-and-redirect';
import { redirectTo404 } from '../../actions/redirect-to-404';
import { fetchCategoryPosts } from '../../actions/fetch-category-posts';
import { setIsCategoryLoaded } from '../../actions/set-is-category-loaded';
import { incrementPostLikeCount } from '../../actions/increment-post-like-count';
import { pageOpened } from '../../actions/page-opened';
import { emitOpenCategory } from '../../actions/category-socket';
import { setIsLoaded } from '../../actions/set-is-loaded';
import Page from '../../components/page';
import CategoryDescription from '../../components/category-description';
import PostList from '../../components/post-list';
import { getSortedPostsByCategoryId, getPostsByCategoryIdAndPage } from '../../selectors/post-selectors';
import AccountSuspended from '../../components/account-suspended';
import Restriction from '../../components/restriction';
import RestrictionPrivateMember from '../../components/restriction-private-member';
import PageMetadata from '../../components/page-metadata';
import AnimatedLoader from '../../components/animated-loader';
import NotFound from '../../components/not-found';
import NoPosts from '../../components/no-posts';
import { FullHeightLayout, SimpleLayout, EmptyLayout } from '../../components/layout';
import AppLoaded from '../../components/app-loaded';
import NewPostMessageRoot from '../new-post-message-root';
import { POSTS_PER_PAGE } from '../../constants/pagination';
import { LAYOUT_TYPE_PATH } from '../../constants/wix-params';
import { LAYOUT_TYPE_CLASSIC } from '../../constants/layout-types';
import withAuth from '../../hoc/with-auth';
import forDevice from '../../hoc/for-device';
import withAppSettings from '../../hoc/with-app-settings';
import { getCategoryBySlug } from '../../selectors/categories-selectors';
import { getIsCategoryLoading } from '../../selectors/is-loading-selectors';
import { getIsCategoryLoaded, getIsPostsLoaded } from '../../selectors/is-loaded-selectors';
import outerHeight from '../../services/outer-height';
import getScrollOffset from '../../services/get-scroll-offset';
import scrollTo from '../../services/scroll-to';
import { FEED_PAGE } from '../../services/detect-route';
import { push } from '../../services/router-utils';
import { getEntityCount } from '../../selectors/pagination-selectors';
import { getStyleSetting, getIsHeaderEnabled } from '../../selectors/app-settings-selectors';
import { isMemberAreaActive } from '../../selectors/member-area-selectors';

const Layout = EmptyLayout;
const NotFoundLayout = forDevice(FullHeightLayout, SimpleLayout);

const resetPage = () => ({ page: 1 });
const incrementPage = state => ({ page: state.page + 1 });

export class CategoryPage extends Component {
  static fetchData = ({ store, params, search, user, query }) => {
    return store
      .dispatch(fetchCategoryAndRedirect(params.categorySlug, search))
      .then(category => {
        const isClassicLayout = getStyleSetting(store.getState(), LAYOUT_TYPE_PATH);
        if (!category.isPrivate || user) {
          return store.dispatch(
            fetchCategoryPosts({
              categoryId: category._id,
              page: isClassicLayout ? _.toNumber(query.page) || 1 : 1,
              pageSize: POSTS_PER_PAGE,
            })
          );
        }
      })
      .catch(() => store.dispatch(redirectTo404(search)));
  };

  constructor(props) {
    super(props);
    this.state = {
      isAnimated: !props.allPosts.length,
      page: (props.layoutType === LAYOUT_TYPE_CLASSIC) ? props.page : 1,
    };
  }

  componentDidMount() {
    const { category, isPostsLoaded } = this.props;
    this.props.pageOpened({
      page: FEED_PAGE,
      category,
    });
    this.props.emitOpenCategory(category);

    if (!isPostsLoaded) {
      this.fetchPosts();
    }
  }

  componentWillUnmount() {
    const { category } = this.props;
    this.props.setIsCategoryLoaded(category._id, false);
    this.props.setIsLoaded('posts', category._id, false);
  }

  componentWillReceiveProps({ categorySlug, category, newPostId, page }) {
    const currentCategorySlug = this.props.categorySlug;

    if (currentCategorySlug !== categorySlug) {
      this.props.emitOpenCategory(category);
      this.setState(resetPage, () => this.fetchPosts(category._id));
    } else if (this.state.page !== page && this.props.page !== page) {
      this.setState({ page }, () => this.fetchPosts(category._id));
    }

    if (newPostId && newPostId !== this.props.newPostId) {
      const postElement = document.getElementById(newPostId);
      if (!postElement) {
        return;
      }

      const rect = postElement.getBoundingClientRect();
      const height = outerHeight(postElement);
      if (rect.top < 0) {
        scrollTo(getScrollOffset() + height);
      }
    }
  }

  componentDidUpdate({ isAuthenticated, isBlocked, isPrivate }) {
    if (
      this.props.isAuthenticated !== isAuthenticated ||
      this.props.isBlocked !== isBlocked ||
      this.props.isPrivate !== isPrivate
    ) {
      this.fetchPosts();
    }
  }

  fetchPosts = (categoryId = this.props.category._id, page = this.state.page) => {
    if (!this.shouldShowPosts()) {
      return;
    }

    this.props.fetchPosts({
      categoryId,
      page,
      pageSize: POSTS_PER_PAGE,
    });
  };

  handleLikeClick = postId => this.props.incrementPostLikeCount(postId);

  shouldShowPosts() {
    const { category, isAuthenticated, isBlocked, isPrivate, isMemberAreaActive } = this.props;
    const hasPrivateSocialProfile = isMemberAreaActive && isPrivate;
    const canSeePrivateCategory = isAuthenticated && !isBlocked && !hasPrivateSocialProfile;
    return !category.isPrivate || canSeePrivateCategory;
  }

  changePage = page => {
    const { categorySlug, category } = this.props;
    this.fetchPosts(category._id, page);
    this.props.push({
      pathname: `/${categorySlug}`,
      query: { page },
    });
  };

  handleLoad = () => this.setState(incrementPage, this.fetchPosts);

  renderPosts() {
    const {
      isBlocked,
      currentPagePosts,
      allPosts,
      entityCount,
      category,
      isLoading,
      isPostsLoaded,
      isAuthenticated,
      isPrivate,
      isMemberAreaActive,
    } = this.props;
    const hasPosts = allPosts.length;

    if (isBlocked) {
      return <AccountSuspended/>;
    } else if (category.isPrivate && !isAuthenticated) {
      return <Restriction/>;
    } else if (category.isPrivate && isMemberAreaActive && isPrivate) {
      return <RestrictionPrivateMember/>;
    } else if (!hasPosts) {
      return <NoPosts category={category}/>;
    } else {
      return (
        <PostList
          page={this.props.page}
          entityCount={entityCount}
          allPosts={allPosts}
          changePage={this.changePage}
          loadMore={this.handleLoad} // eslint-disable-line react/jsx-handler-names
          currentPagePosts={currentPagePosts}
          onLikeClick={this.handleLikeClick}
          isLoading={isLoading}
          category={category}
          isLoaded={isPostsLoaded}
        />
      );
    }
  }

  render() {
    const { category, isLoading, isCategoryLoaded, allPosts, isPostsLoaded, isHeaderEnabled } = this.props;
    if (_.isEmpty(category)) {
      return (
        <Page>
          <NotFoundLayout>
            <NotFound/>
          </NotFoundLayout>
        </Page>
      );
    }

    return (
      <Page>
        <Layout>
          <PageMetadata title={category.label} description={category.description} image={category.cover}/>
          <NewPostMessageRoot pathname={this.props.location.pathname}/>
          <CategoryDescription category={category} isCoverVisible={isHeaderEnabled}/>
          <AnimatedLoader
            isLoading={isLoading && !isCategoryLoaded && !allPosts.length}
            isAnimated={this.state.isAnimated}
          >
            {this.renderPosts()}
          </AnimatedLoader>
        </Layout>
        {isPostsLoaded && <AppLoaded parent={this} selector="[data-hook=post-list]"/>}
      </Page>
    );
  }
}

CategoryPage.propTypes = {
  categorySlug: PropTypes.string.isRequired,
  fetchPosts: PropTypes.func.isRequired,
  emitOpenCategory: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  incrementPostLikeCount: PropTypes.func.isRequired,
  categoryId: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  setIsCategoryLoaded: PropTypes.func.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  push: PropTypes.func,
  entityCount: PropTypes.number,
  page: PropTypes.number,
  allPosts: PropTypes.array,
  currentPagePosts: PropTypes.array,
  category: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  isPrivate: PropTypes.bool,
  params: PropTypes.object,
  t: PropTypes.func,
  newPostId: PropTypes.string,
  isLoading: PropTypes.bool,
  isPostsLoaded: PropTypes.bool,
  isCategoryLoaded: PropTypes.bool,
  layoutType: PropTypes.number,
  isMemberAreaActive: PropTypes.bool,
  isHeaderEnabled: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
  const category = getCategoryBySlug(state, ownProps.params.categorySlug) || {};
  const categoryId = category._id;
  const page = _.toNumber(_.get(ownProps, 'location.query.page', 1));

  return {
    categorySlug: ownProps.params.categorySlug,
    category,
    allPosts: getSortedPostsByCategoryId(state, categoryId),
    currentPagePosts: getPostsByCategoryIdAndPage(state, categoryId, page),
    newPostId: state.newPostMessage,
    entityCount: getEntityCount(state, 'posts'),
    categoryId,
    isLoading: getIsCategoryLoading(state, categoryId),
    isCategoryLoaded: getIsCategoryLoaded(state, categoryId),
    isPostsLoaded: getIsPostsLoaded(state, categoryId),
    isMemberAreaActive: isMemberAreaActive(state),
    page,
    isHeaderEnabled: getIsHeaderEnabled(state),
  };
};

const mapDispatchToProps = {
  fetchPosts: fetchCategoryPosts,
  setIsCategoryLoaded,
  setIsLoaded,
  incrementPostLikeCount,
  emitOpenCategory,
  pageOpened,
  push,
};

export default _.flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withAuth,
  withAppSettings({ layoutType: LAYOUT_TYPE_PATH })
)(CategoryPage);



// WEBPACK FOOTER //
// ./app/containers/category-page/category-page.js